import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";

const EstimationPrint = React.forwardRef((props, ref) => {
  const printbody = {
    display: "flex",
    flexDirection: "column",
    margin: "10px",
    fontFamily: "Arial, sans-serif",
    fontSize: "12px",
  };

  // const { data } = props;
  const { data, printSections = {} } = props;
  const show = (key) => printSections[key] !== false; 
  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Calculate total weights for specific components
  const buildingData = {};

  // Only process the components we care about
  const targetComponents = [
    "builtUpMembers",
    "leftSideWall",
    "rightSideWall",
    "frontEndWall",
    "backEndWall",
    "roof01",
    "roof02",
    "pipeStrut",
    "pipeBracing",
    "hR",
    "portalBracing",
    "leanTo",
    "canopy",
    "frameOpening",
    "partitionWall",
    "turboFans",
    "craneBeam",
    "mezzanine",
    "fascia",
    "louvers",
    "structuralLouver",
    "industrialLouver",
    "staircase",
    "handrail",
    "hotRolled",
    "others",
  ];
  const componentLabels = {
    builtUpMembers: "Built Up Members",
    leftSideWall: "Left Side Wall",
    rightSideWall: "Right Side Wall",
    frontEndWall: "Front End Wall",
    backEndWall: "Back End Wall",
    roof01: "Roof 01",
    roof02: "Roof 02",
    pipeStrut: "Pipe Strut",
    pipeBracing: "Pipe Bracing",
    hR: "Hot Rolled",
    portalBracing: "Portal Bracing",
    leanTo: "Lean-To",
    canopy: "Canopy",
    frameOpening: "Frame Opening",
    partitionWall: "Partition Wall",
    turboFans: "Turbo Fans",
    craneBeam: "Crane Beam",
    mezzanine: "Mezzanine",
    fascia: "Fascia",
    louvers: "Louvers",
    structuralLouver: "Structural Louver",
    industrialLouver: "Industrial Louver",
    staircase: "Staircase",
    handrail: "Handrail",
    hotRolled: "Hot Rolled",
    others: "Others",
  };
  targetComponents.forEach((componentType) => {
    if (data?.componentBlocks?.[componentType]) {
      Object.keys(data.componentBlocks[componentType]).forEach(
        (buildingKey) => {
          const buildingNum = parseInt(buildingKey);

          if (!buildingData[buildingNum]) {
            buildingData[buildingNum] = {};
          }

          const items = data.componentBlocks[componentType][buildingKey];

          const quantity = items.reduce(
            (sum, item) => sum + Number(item?.quantity || 0),
            0
          );

          const totalPrice = items.reduce(
            (sum, item) => sum + Number(item?.totalPrice || 0),
            0
          );

          const totalWeight = items.reduce(
            (sum, item) => sum + Number(item?.totalWeight || 0),
            0
          );
          const unitPrice = items.reduce(
            (sum, item) => sum + Number(item?.unitPrice || 0),
            0
          );
          // Store both quantity & price
          buildingData[buildingNum][componentType] = {
            quantity: Number(quantity),
            totalPrice: Number(totalPrice),
            totalWeight: Number(totalWeight),
            unitPrice: Number(unitPrice),
          };
        }
      );
    }
  });
  const componentBlocks = data?.componentBlocks || {};
  // Get building information
  // const buildings = data?.additionalComponent?.buildings || [];

  const buildings = data?.buildings || data?.additionalComponent?.buildings || [];


  // Simplified version - use only materialGroup

  const formatNumber = (value, decimals = 2) => {
    const num = Number(value);
    return isNaN(num) ? "0.000" : num.toFixed(decimals);
  };

  //building area
  const [getTotalBuildingArea, setTotalBuildingArea] = useState(0);

  useEffect(() => {
    if (!data?.buildings) return;

    const total = data.buildings.reduce((sum, b) => {
      return sum + (Number(b?.buildingArea) || 0);
    }, 0);

    setTotalBuildingArea(total);
  }, [data]);
  //c1 c2 showing
  const formatHeights = (obj) => {
    if (!obj || typeof obj !== "object") {
      return ""; // safe fallback instead of error
    }

    return Object.entries(obj)
      .filter(([key]) => /^c\d+$/.test(key)) // Only c1, c2, c3
      .map(([_, value]) => `1@${Number(value).toFixed(3)}m`)
      .join(",");
  };

  // for 3 buildings caluclations

  const calculateCombinedData = (
    buildingData,
    targetComponents,
    componentLabels
  ) => {
    const combinedData = {};
    let combinedGrandTotalWeight = 0;
    let combinedGrandTotalPrice = 0;

    targetComponents.forEach((componentType) => {
      combinedData[componentType] = {
        description: componentLabels[componentType],
        totalWeight: 0,
        unitPrice: 0,
        totalPrice: 0,
        count: 0,
      };
    });

    Object.keys(buildingData).forEach((buildingNum) => {
      Object.keys(buildingData[buildingNum]).forEach((componentType) => {
        const component = buildingData[buildingNum][componentType];
        if (component && combinedData[componentType]) {
          combinedData[componentType].totalWeight += component.totalWeight;
          combinedData[componentType].totalPrice += component.totalPrice;
          combinedData[componentType].unitPrice = component.unitPrice;
          combinedData[componentType].count += 1;

          combinedGrandTotalWeight += component.totalWeight;
          combinedGrandTotalPrice += component.totalPrice;
        }
      });
    });

    const finalCombinedData = targetComponents
      .map((componentType) => {
        const data = combinedData[componentType];
        if (data && data.count > 0) {
          return {
            ...data,
            totalWeight: data.totalWeight.toFixed(3),
            totalPrice: data.totalPrice.toFixed(3),
            unitPrice: data.unitPrice.toFixed(3),
          };
        }
        return null;
      })
      .filter((item) => item !== null);

    return {
      combinedData: finalCombinedData,
      combinedGrandTotalWeight: combinedGrandTotalWeight.toFixed(3),
      combinedGrandTotalPrice: combinedGrandTotalPrice.toFixed(3),
    };
  };

  const countUniqueBuildings = (buildingData) => {
    return Object.keys(buildingData).length;
  };

  // Assuming these variables are calculated within your main component:
  // const { combinedData, combinedGrandTotalWeight, combinedGrandTotalPrice } = calculateCombinedData(buildingData, targetComponents, componentLabels);
  // const totalBuildings = countUniqueBuildings(buildingData);
  // const totalBuildingArea = parseFloat(getTotalBuildingArea);
  // const grandTotalWeight = parseFloat(combinedGrandTotalWeight);

  const { combinedData, combinedGrandTotalWeight, combinedGrandTotalPrice } =
    calculateCombinedData(buildingData, targetComponents, componentLabels);
  const totalBuildings = countUniqueBuildings(buildingData);
  const totalBuildingArea = parseFloat(getTotalBuildingArea);
  const grandTotalWeight = parseFloat(combinedGrandTotalWeight);

  // Individual building area (Sqm)
  // const getIndividualBuildingArea = (buildingIndex) => {
  //   const building = buildings[buildingIndex - 1];
  //   return building?.buildingArea ?? 0;
  // };

  const getIndividualBuildingArea = (buildingIndex) => {
    const building = buildings[buildingIndex - 1];
    return Number(building?.totalArea || building?.buildingArea || 0);
};

  // Individual building area (Sqft)
  const getIndividualBuildingAreaSqft = (buildingIndex) => {
    return getIndividualBuildingArea(buildingIndex) * 10.764;
  };

  // Individual building total weight (Kg)
  const getIndividualBuildingWeight = (buildingNum) => {
    const buildingObj = buildingData[buildingNum];
    if (!buildingObj) return 0;

    return targetComponents.reduce((sum, comp) => {
      return sum + (buildingObj[comp]?.totalWeight || 0);
    }, 0);
  };

  // Individual Kg per Sqft
  const getIndividualKgPerSqft = (buildingIndex, buildingNum) => {
    const weight = getIndividualBuildingWeight(buildingNum);
    const areaSqft = getIndividualBuildingAreaSqft(buildingIndex);
    return areaSqft > 0 ? weight / areaSqft : 0;
  };

  const MATERIAL_GROUPS = {
    BUILT_UP: {
      label: "BuiltUp Members",
      match: (code) =>
        code.startsWith("BUWT") ||
        code.startsWith("CONWT") ||
        code.startsWith("BUF") ||
        code.startsWith("BUW") ||
        // code.startsWith("CPT") ||
        code.startsWith("BU"),
    },
    CONNECTION_PLATE: {
  label: "Builtup Connection Plate",
  match: (code) => code.startsWith("CPT"),
},

    HOT_ROLLED: {
      label: "Hot Rolled Sections",
      match: (code) =>
        code.startsWith("ISMB") ||
        code.startsWith("ISMC") ||
        code.startsWith("ISWB") ||
        code.startsWith("ISJB") ||
        code.startsWith("ISEQA") ||
        code.startsWith("ISLB") ||
        code.startsWith("ISHB") ||
        code.startsWith("ISA") ||
        code.startsWith("ISJC") ||
        code.startsWith("ISLC") ||
        code.startsWith("APRHS") ||
        code.startsWith("APCHS") ||
        code.startsWith("APSHS") ||
        code.startsWith("ISUEQA") ||
        // code.startsWith("HB") ||
        code.startsWith("SRD")||
        (code.startsWith("HB") && 
      !code.startsWith("HBZ") && 
      !code.startsWith("HBNZ") && 
      !code.startsWith("HBC") && 
      !code.startsWith("HBEA") && 
      !code.startsWith("HBSA") && 
      !code.startsWith("HBSFB")) ||
    code.startsWith("SRD"),
       
    },

    CR: {
      label: "Cold Formed (CR)",
      match: (code) =>
        code.startsWith("HBZ") ||
        code.startsWith("HBNZ") ||
        code.startsWith("HBC") ||
        code.startsWith("INC") ||
        code.startsWith("INZ") ||
        code.startsWith("HBEA") ||
        code.startsWith("HBSA") ||
        code.startsWith("ISC") ||
        code.startsWith("ISZ") ||
        code.startsWith("HBSFB") ||
         code.startsWith("SA252.0") ||
        code.startsWith("FB502.0"),
    },

    SHEETING: {
      label: "Sheeting",
      match: (code) =>
        code.startsWith("ROOFPPG") ||
        code.startsWith("ROOFBG") ||
        code.startsWith("CLADBG") ||
        code.startsWith("CLADPPG") ||
        code.startsWith("CLADGL") ||
        code.startsWith("TR")||
        code.startsWith("CLADPPGL")||
        code.startsWith("ROOFPPGL")||
        code.startsWith("ROOFBGL"),
    },

    FLASHINGS: {
      label: "Flashings",
      match: (code) =>
        code.startsWith("GUTTER") ||
        code.startsWith("VGUTTER") ||
        code.startsWith("GUTTEREC") ||
        code.startsWith("GUTTERSTRP") ||
        code.startsWith("RDFLASH") ||
        code.startsWith("BGCAP") ||
        code.startsWith("GBANGLE") ||
        code.startsWith("EDANGLE") ||
        code.startsWith("DRFLASH") ||
        code.startsWith("ICORFLASH") ||
        code.startsWith("OCORFLASH") ||
        code.startsWith("APRON") ||
        code.startsWith("CANOPYBOTTOMFLASH") ||
        code.startsWith("DOWNSPOUT") ||
        code.startsWith("DOWNSPOUTSTRP") ||
        code.startsWith("CGLADDER") ||
        code.startsWith("STAIRCASE"),
    },

    ANCHOR_BOLTS: {
      label: "Anchor Bolts",
      match: (code) => code.startsWith("AB"),
    },

    MS_HT_BOLTS: {
      label: "MS / HT Bolts",
      match: (code) => code.startsWith("MSB") || code.startsWith("HTB"),
    },

    
    // BUILTUP_CONNECTION_PLATE: {
    //   label: "Builtup Connection Plate",
    //   match: (code) => code.startsWith("CPT") ,
    // },
    // BUILTUP_PLATE: {
    //   label: "Builtup Plate",
    //   match: (code) => code.startsWith("BUPT"),
    // },
  };

  // const getGroupWiseSummaryByBuilding = (componentBlocks, buildingNo) => {
  //   const result = {};

  //   // init groups
  //   Object.keys(MATERIAL_GROUPS).forEach((groupKey) => {
  //     result[groupKey] = {
  //       label: MATERIAL_GROUPS[groupKey].label,
  //       totalWeight: 0,
  //     };
  //   });

  //   Object.keys(componentBlocks).forEach((componentKey) => {
  //     const items = componentBlocks[componentKey]?.[buildingNo];
  //     if (!Array.isArray(items)) return;

  //     items.forEach((item) => {
  //       const code = item.code || "";
  //       const weight = Number(item.totalWeight || 0);

  //       Object.keys(MATERIAL_GROUPS).forEach((groupKey) => {
  //         if (MATERIAL_GROUPS[groupKey].match(code)) {
  //           result[groupKey].totalWeight += weight;
  //         }
  //       });
  //     });
  //   });

  //   return result;
  // };

  // const getBuiltUpMaterialWiseByBuilding = (componentBlocks, buildingNo) => {
  //   const materialMap = {};
  //   let totalBuiltUpWeight = 0;

  //   const builtUpData = componentBlocks?.builtUpMembers?.[buildingNo];

  //   if (!Array.isArray(builtUpData)) {
  //     return {
  //       materials: [],
  //       totalWeight: 0,
  //     };
  //   }

  //   builtUpData.forEach((item) => {
  //     const key = `${item.code}__${item.description}`;
  //     const weight = Number(item.totalWeight || 0);

  //     if (!materialMap[key]) {
  //       materialMap[key] = {
  //         code: item.code,
  //         description: item.description,
  //         totalWeight: 0,
  //         totalPrice: 0,
  //       };
  //     }

  //     materialMap[key].totalWeight += weight;
  //     materialMap[key].totalPrice += Number(item.totalPrice || 0);
  //     totalBuiltUpWeight += weight;
  //   });

  //   return {
  //     materials: Object.values(materialMap),
  //     totalWeight: totalBuiltUpWeight,
  //   };
  // };

  // const getBuiltUpKgPerSqft = (totalWeight, buildingSqft) => {
  //   if (!buildingSqft || buildingSqft === 0) return 0;
  //   return totalWeight / buildingSqft;
  // };

  const SQM_TO_SFT = 10.764;

  // const getGroupWiseSummaryByBuilding = (componentBlocks, buildingNo) => {
  //   const result = {};

  //   // ✅ get area correctly from buildings[]
  //   const buildingAreaSqm = getIndividualBuildingArea(Number(buildingNo));
  //   const buildingAreaSft = buildingAreaSqm * SQM_TO_SFT;

  //   // init groups
  //   Object.keys(MATERIAL_GROUPS).forEach((groupKey) => {
  //     result[groupKey] = {
  //       label: MATERIAL_GROUPS[groupKey].label,
  //       totalWeight: 0,
  //       areaSqm: buildingAreaSqm,
  //       areaSft: buildingAreaSft,
  //       kgPerSft: 0,
  //     };
  //   });

  //   Object.keys(componentBlocks).forEach((componentKey) => {
  //     const items = componentBlocks[componentKey]?.[buildingNo];
  //     if (!Array.isArray(items)) return;

  //     items.forEach((item) => {
  //       const code = item.code || "";
  //       const weight = Number(item.totalWeight || 0);

  //       Object.keys(MATERIAL_GROUPS).forEach((groupKey) => {
  //         if (MATERIAL_GROUPS[groupKey].match(code)) {
  //           result[groupKey].totalWeight += weight;
  //         }
  //       });
  //     });
  //   });

  //   // ✅ calculate Kg / Sft
  //   Object.keys(result).forEach((groupKey) => {
  //     const group = result[groupKey];
  //     if (group.areaSft > 0) {
  //       group.kgPerSft = group.totalWeight / group.areaSft;
  //     }
  //   });

  //   return result;
  // };
// MATERIAL_GROUPS hardcoded object అవసరమే లేదు
// items లో directly item.materialGroup వాడు

// const getGroupWiseSummaryByBuilding = (componentBlocks, buildingNo) => {
//   const result = {};
//   const buildingAreaSqm = getIndividualBuildingArea(Number(buildingNo));
//   const buildingAreaSft = buildingAreaSqm * SQM_TO_SFT;

//   // init all groups including OTHERS
//   [...Object.keys(MATERIAL_GROUPS), "OTHERS"].forEach((groupKey) => {
//     result[groupKey] = {
//       label: MATERIAL_GROUPS[groupKey]?.label || "Others",
//       totalWeight: 0,
//       areaSqm: buildingAreaSqm,
//       areaSft: buildingAreaSft,
//       kgPerSft: 0,
//     };
//   });

//   Object.keys(componentBlocks).forEach((componentKey) => {
//     const items = componentBlocks[componentKey]?.[buildingNo];
//     if (!Array.isArray(items)) return;

//     items.forEach((item) => {
//       const code = item.code || "";
//       const weight = Number(item.totalWeight || 0);

//       //  first-match only — no double count
//       const matchedKey =
//         Object.keys(MATERIAL_GROUPS).find((gk) =>
//           MATERIAL_GROUPS[gk].match(code)
//         ) || "OTHERS";

//       result[matchedKey].totalWeight += weight;
//     });
//   });

//   Object.keys(result).forEach((gk) => {
//     if (result[gk].areaSft > 0) {
//       result[gk].kgPerSft = result[gk].totalWeight / result[gk].areaSft;
//     }
//   });

//   return result;
// };

const getGroupWiseSummaryByBuilding = (componentBlocks, buildingNo) => {
  const result = {};
  const buildingAreaSqm = getIndividualBuildingArea(Number(buildingNo));
  const buildingAreaSft = buildingAreaSqm * SQM_TO_SFT;

  [...Object.keys(MATERIAL_GROUPS), "OTHERS"].forEach((groupKey) => {
    result[groupKey] = {
      label: MATERIAL_GROUPS[groupKey]?.label || "Others",
      totalWeight: 0,
      areaSqm: buildingAreaSqm,
      areaSft: buildingAreaSft,
      kgPerSft: 0,
    };
  });

  // ✅ FIX: targetComponents మాత్రమే process చేయి
  Object.keys(componentBlocks)
    .filter((componentKey) => targetComponents.includes(componentKey)) // ← ఇది add చేయి
    .forEach((componentKey) => {
      const items = componentBlocks[componentKey]?.[buildingNo];
      if (!Array.isArray(items)) return;

      items.forEach((item) => {
        const code = item.code || "";
        const weight = Number(item.totalWeight || 0);

        const matchedKey =
          Object.keys(MATERIAL_GROUPS).find((gk) =>
            MATERIAL_GROUPS[gk].match(code)
          ) || "OTHERS";

        result[matchedKey].totalWeight += weight;
      });
    });

  Object.keys(result).forEach((gk) => {
    if (result[gk].areaSft > 0) {
      result[gk].kgPerSft = result[gk].totalWeight / result[gk].areaSft;
    }
  });

  return result;
};

  const getMaterialBreakupByGroup = (groupKey, items) => {
    const groupByWithArea = (filteredItems, keyGetter ,skipArea = false) => {
      const group = {};

      filteredItems.forEach((item) => {
        const key = keyGetter(item);

        const pm = Number(item.pieceMeasure) || 0;
        const qty = Number(item.quantity) || 0;
        const rawAvg = Number(item.avg);
        const avg = rawAvg && rawAvg > 0 ? rawAvg : 1;
        // const itemArea = pm * qty * avg;
        const itemArea = skipArea ? 0 : pm * qty * avg;


        if (!group[key]) {
          group[key] = {
            weight: 0,
            quantity: 0,
            area: 0,
            // JSON nundi original unit ni ikkada store chestunnam
            unit: item.unit || "",
            thickness: item.thickness || "-",
            materialDescription: item.description || "",
          };
        }

        group[key].weight += Number(item.totalWeight) || 0;
        group[key].quantity += Number(item.quantity) || 0;
        group[key].area += itemArea;
      });
      return group;
    };

    switch (groupKey) {
      case "BUILT_UP":
        return {
          BUW_BUF: groupByWithArea(
            items.filter(
              (i) => i.code?.startsWith("BUW") || i.code?.startsWith("BUF")
            ),
            (i) => i.code
          ),
          OTHER_BUILTUP: groupByWithArea(
            items.filter(
              (i) => !i.code?.startsWith("BUW") && !i.code?.startsWith("BUF")
            ),
            (i) => i.code,
            true
          ),
        };
        // case "CONNECTION_PLATE":
        case "CONNECTION_PLATE":
    return groupByWithArea(items, (i) => i.code, true);
      case "CR":
      case "HOT_ROLLED":
      case "SHEETING":
      case "FLASHINGS":
      case "ANCHOR_BOLTS":
      case "MS_HT_BOLTS":
        // return groupByWithArea(items, (i) => i.code);
        return groupByWithArea(items, (i) => i.code);
      default:
        return {};
    }
  };

  const getGroupItemsForBuilding = (componentBlocks, buildingNo) => {
  const result = {};

  Object.keys(componentBlocks)
    .filter((componentKey) => targetComponents.includes(componentKey))
    .forEach((componentKey) => {
      const items = componentBlocks[componentKey]?.[buildingNo];
      if (!Array.isArray(items)) return;

      items.forEach((item) => {
        const code = item.code || "";
        const matchedKey =
          Object.keys(MATERIAL_GROUPS).find((gk) =>
            MATERIAL_GROUPS[gk].match(code)
          ) || "OTHERS";

        if (!result[matchedKey]) result[matchedKey] = [];
        result[matchedKey].push(item);
      });
    });

  return result;
};

// const getGroupItemsForBuilding = (componentBlocks, buildingNo) => {
//   const result = {};

//   Object.values(componentBlocks).forEach((component) => {
//     const items = component?.[buildingNo];
//     if (!Array.isArray(items)) return;

//     items.forEach((item) => {
//       const code = item.code || "";

//       //  first-match only
//       const matchedKey =
//         Object.keys(MATERIAL_GROUPS).find((gk) =>
//           MATERIAL_GROUPS[gk].match(code)
//         ) || "OTHERS";

//       if (!result[matchedKey]) result[matchedKey] = [];
//       result[matchedKey].push(item);
//     });
//   });

//   return result;
// };

  // const getGroupItemsForBuilding = (componentBlocks, buildingNo) => {
  //   const result = {};

  //   Object.keys(MATERIAL_GROUPS).forEach((groupKey) => {
  //     result[groupKey] = [];
  //   });

  //   Object.values(componentBlocks).forEach((component) => {
  //     const items = component?.[buildingNo];
  //     if (!Array.isArray(items)) return;

  //     items.forEach((item) => {
  //       const code = item.code || "";

  //       Object.entries(MATERIAL_GROUPS).forEach(([groupKey, cfg]) => {
  //         if (cfg.match(code)) {
  //           result[groupKey].push(item);
  //         }
  //       });
  //     });
  //   });

  //   return result;
  // };

  const combinedMaterialMap = {};

  Object.values(data?.componentBlocks || {}).forEach((componentObj) => {
    Object.values(componentObj || {}).forEach((buildingItems) => {
      (buildingItems || []).forEach((item) => {
        const code = item.materialCode || item.code || "NA";
        const desc = item.materialDescription || item.description || "NA";
        const key = `${code}__${desc}`;

        if (!combinedMaterialMap[key]) {
          combinedMaterialMap[key] = {
            materialCode: code,
            description: desc,
            totalWeight: 0,
          };
        }

        combinedMaterialMap[key].totalWeight += Number(item.totalWeight || 0);
      });
    });
  });

  const combinedMaterialList = Object.values(combinedMaterialMap).filter(
    (m) => m.totalWeight > 0
  );

const groupedMaterials = combinedMaterialList.reduce((acc, current) => {
  const code = current.materialCode;
  
  if (!acc[code]) {
    acc[code] = { 
      materialCode: code, 
      description: current.description.split('(')[1]?.replace(')', '') || code, // Optional: cleans description
      totalWeight: 0 
    };
  }
  
  acc[code].totalWeight += current.totalWeight;
  
  return acc;
}, {});

const groupedMaterialList = Object.values(groupedMaterials);

groupedMaterialList.sort((a, b) => a.materialCode.localeCompare(b.materialCode));
  const totalSqftAllBuildings = totalBuildingArea * 10.764;

  const baySpacingSummary = (rows = []) => {
    const countMap = rows.reduce((acc, row) => {
      const value = row?.baySpacing;
      if (value) {
        acc[value] = (acc[value] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(countMap)
      .map(([value, count]) => `${count}@${value}`)
      .join(", ");
  };

//   return (
//     <div ref={ref} style={printbody} className="m-4">
//       {/* Header Section */}
//       <Row className="mb-3">
//         <Col>
//           <div className="d-flex align-items-center justify-content-between">
//             <img
//               src="/assets/img/HB/halleysblue.jpeg"
//               alt="HalleysBlue Steels"
//               className="headerLogo"
//               style={{ height: "60px" }}
//             />
//             <div className="text-center">
//               <h4 className="mb-0">HalleysBlue Steels Pvt Ltd</h4>
//               <small>Pre Engineered Buildings</small>
//             </div>
//             <div style={{ width: "100px" }}></div> {/* Spacer for alignment */}
//           </div>
//         </Col>
//       </Row>

//       <hr className="my-2" />

//       {/* Document Details */}
//       <Row className="mb-3">
//         <Col className="d-flex justify-content-center">
//           <table style={{ width: "100%", borderCollapse: "collapse" }}>
//             <tbody>
//               <tr>
//                 <td style={{ padding: "2px 0" }}>
//                   <span className="px-2">
//                     {" "}
//                     <strong>Client Name:</strong> {data?.clientName}
//                   </span>
//                 </td>
//                 <td style={{ padding: "2px 0", textAlign: "right" }}>
//                   <span className="px-2">
//                     <strong className="px-2">Date:</strong>{" "}
//                     {formatDate(data?.createdAt)}
//                   </span>
//                 </td>
//               </tr>
//               <tr>
//                 <td style={{ padding: "2px 0" }}>
//                   <span className="px-2">
//                     {" "}
//                     <strong>Description:</strong> Estimate for the proposed PEB
//                     @{data?.location}
//                   </span>
//                 </td>
//                 <td style={{ padding: "2px 0", textAlign: "right" }}>
//                   <span className="px-2">
//                     {" "}
//                     <strong>Rev:</strong> {data?.version || "v0"}
//                   </span>
//                 </td>
//               </tr>
//               <tr>
//                 <td style={{ padding: "2px 0" }}></td>
//                 <td style={{ padding: "2px 0", textAlign: "right" }}>
//                   <span className="px-2">
//                     <strong>Prep By:</strong> {data?.designerName}
//                   </span>
//                 </td>
//               </tr>
//             </tbody>
//           </table>
//         </Col>
//       </Row>
//       <Row>
//         {data?.buildings?.map((item) => (
//           <>
//             <Col md={12} className="mb-2">
//               <div
//                 style={{
//                   border: "1px solid black",
//                   padding: "8px",
//                   height: "100%",
//                 }}
//               >
//                 <strong>Building Specifications : {item?.buildingTypes}</strong>
//                 <table
//                   style={{
//                     width: "100%",
//                     borderCollapse: "collapse",
//                     marginTop: "4px",
//                   }}
//                 >
//                   <tbody>
//                     <tr>
//                       <td>1</td>
//                       <td>Width O/O</td>
//                       <td>
//                         {item?.front?.meters} m {""}{" "}
//                       </td>
//                     </tr>
//                     {/* <tr>
//                       <td></td>
//                       <td>Bay Spacing11</td>
//                       <td>
//                         {item?.front?.bays}@{item?.front?.rows?.[0]?.baySpacing}
//                       </td>
//                     </tr> */}
//                     <tr>
//                       <td></td>
//                       <td>Bay Spacing</td>
//                       <td>
//                         {baySpacingSummary(item?.front?.rows)} m {""}
//                       </td>
//                     </tr>

//                     <tr>
//                       <td>2</td>
//                       <td>Length O/O</td>
//                       <td>
//                         {item?.left?.meters} m {""}{" "}
//                       </td>
//                     </tr>
//                     <tr>
//                       <td></td>
//                       <td>Bay Spacing</td>
//                       <td>
//                         {item?.left?.bays}@{item?.left?.rows?.[0]?.baySpacing} m{" "}
//                         {""}
//                       </td>
//                     </tr>
//                     <tr>
//                       <td>3</td>
//                       <td>Eave height</td>
//                       <td>
//                         {item?.selectedCaseObject?.eaveHeightLSW && (
//                           <>
//                             LSW : {item?.selectedCaseObject?.eaveHeightLSW} m{" "}
//                           </>
//                         )}
//                         {item?.selectedCaseObject?.eaveHeightRSW && (
//                           <>RSW : {item?.selectedCaseObject?.eaveHeightRSW} m</>
//                         )}
//                       </td>
//                     </tr>
//                     <tr>
//                       <td>4</td>
//                       <td>No of interior MS</td>
//                       <td>
//                         {item?.mainBuildingTypeCode === "MSB" ||
//                         item?.subBuildingTypeCode === "MSB" ? (
//                           <span>{formatHeights(item?.selectedCaseObject)}</span>
//                         ) : (
//                           <span>Nill</span>
//                         )}
//                       </td>
//                     </tr>

//                     <tr>
//                       <td>4</td>
//                       <td>Roof Bracings</td>
//                       <td>{item?.roofBracing}</td>
//                     </tr>
//                     <tr>
//                       <td>5</td>
//                       <td>Side Bracing</td>
//                       <td>{item?.sideBracing}</td>
//                     </tr>

//                     <tr>
//                       <td>7</td>
//                       <td>Open Wall / Masonry Conditions</td>
//                       <td>{item?.openWallMasonaryConditions}</td>
//                     </tr>
//                     <tr>
//                       <td>8</td>
//                       <td>Ridge Height (Slope)</td>
//                       <td>
//                         {item?.selectedCaseObject?.ridgeHeight || "--"} m {""}
//                       </td>
//                     </tr>
//                   </tbody>
//                 </table>
//               </div>
//             </Col>
//             <Col>
//               <div
//                 style={{
//                   border: "1px solid black",
//                   padding: "8px",
//                   height: "100%",
//                 }}
//               >
//                 <strong>Loading Details</strong>

//                 <table
//                   style={{
//                     width: "100%",
//                     borderCollapse: "collapse",
//                     marginTop: "4px",
//                   }}
//                 >
//                   <tbody>
//                     {(() => {
//                       const load = item?.designLoad || {};

//                       return (
//                         <>
//                           {/* Static rows */}
//                           <tr>
//                             <td>Dead Load (kn/Sq.m)</td>
//                             <td>{load?.deadLoad || "--"}</td>
//                           </tr>
//                           <tr>
//                             <td>Live Load (kn/Sq.m)</td>
//                             <td>{load?.liveLoad || "--"}</td>
//                           </tr>
//                           <tr>
//                             <td>Solar Load (kn/Sq.m)</td>
//                             <td>{load?.solarLoad || "--"}</td>
//                           </tr>
//                           <tr>
//                             <td>Wind Load (kmph)</td>
//                             <td>{load?.windLoad || "--"}</td>
//                           </tr>

//                           {/* Dynamic rows: designLoad1 … designLoad100 */}
//                           {Object.entries(load)
//                             .filter(([key]) => key.startsWith("designLoad"))
//                             .sort(
//                               (a, b) =>
//                                 Number(a[0].replace("designLoad", "")) -
//                                 Number(b[0].replace("designLoad", ""))
//                             )
//                             .map(([key, value], i) => (
//                               <tr key={i}>
//                                 <td>
//                                   {key.replace("designLoad", "Design Load ")}
//                                 </td>
//                                 <td>{value || "--"}</td>
//                               </tr>
//                             ))}
//                         </>
//                       );
//                     })()}
//                   </tbody>
//                 </table>
//               </div>
//             </Col>

//             <Col md={12} className="mb-2">
//               <div style={{ border: "1px solid black", padding: "8px" }}>
//                 <strong>Design Standards</strong>

//                 <table
//                   style={{
//                     width: "100%",
//                     borderCollapse: "collapse",
//                     marginTop: "4px",
//                   }}
//                 >
//                   <tbody>
//                     {(() => {
//                       const ds = item?.designStandards || {};

//                       return (
//                         <>
//                           {/* Static Rows */}
//                           <tr>
//                             <td>1</td>
//                             <td>Codes Applicable</td>
//                             <td>{ds?.codesApplicable || "--"}</td>
//                           </tr>

//                           <tr>
//                             <td>2</td>
//                             <td>Wind Load Calculation</td>
//                             <td>{ds?.windLoadCalculation || "--"}</td>
//                           </tr>

//                           <tr>
//                             <td>3</td>
//                             <td>Wind Load Application</td>
//                             <td>{ds?.windLoadApplication || "--"}</td>
//                           </tr>

//                           <tr>
//                             <td>4</td>
//                             <td>Seismic Load</td>
//                             <td>{ds?.seismicLoad || "--"}</td>
//                           </tr>

//                           {/* Dynamic Fields: designStandard1…designStandard100 */}
//                           {Object.entries(ds)
//                             .filter(([key]) => key.startsWith("designStandard"))
//                             .sort(
//                               (a, b) =>
//                                 Number(a[0].replace("designStandard", "")) -
//                                 Number(b[0].replace("designStandard", ""))
//                             )
//                             .map(([key, value], index) => (
//                               <tr key={key}>
//                                 <td>{index + 5}</td>
//                                 <td>
//                                   {key.replace(
//                                     "designStandard",
//                                     "Design Standard "
//                                   )}
//                                 </td>
//                                 <td>{value || "--"}</td>
//                               </tr>
//                             ))}
//                         </>
//                       );
//                     })()}
//                   </tbody>
//                 </table>
//               </div>
//             </Col>

//             <Col md={12}>
//               <div style={{ border: "1px solid black", padding: "8px" }}>
//                 <strong>Deflection Criteria</strong>

//                 <table
//                   style={{
//                     width: "100%",
//                     borderCollapse: "collapse",
//                     marginTop: "4px",
//                   }}
//                 >
//                   <tbody>
//                     {(() => {
//                       const dc = item?.deflectionCriteria || {};

//                       return (
//                         <>
//                           {/* Static Fields */}
//                           <tr>
//                             <td>Main frame (Sway)</td>
//                             <td>{dc?.mainFramesSway || "--"}</td>
//                           </tr>

//                           <tr>
//                             <td>Main frame (Deflection)</td>
//                             <td>{dc?.mainFrameDeflection || "--"}</td>
//                           </tr>

//                           {Object.entries(dc)
//                             .filter(([key]) =>
//                               key.startsWith("deflectionCriteria")
//                             )
//                             .sort(
//                               (a, b) =>
//                                 Number(a[0].replace("deflectionCriteria", "")) -
//                                 Number(b[0].replace("deflectionCriteria", ""))
//                             )
//                             .map(([key, value], index) => (
//                               <tr key={key}>
//                                 <td>
//                                   {key.replace(
//                                     "deflectionCriteria",
//                                     "Deflection Criteria"
//                                   )}
//                                 </td>
//                                 <td>{value || "--"}</td>
//                               </tr>
//                             ))}
//                         </>
//                       );
//                     })()}
//                   </tbody>
//                 </table>
//               </div>
//             </Col>

//             <Col md={12}>
//               <div style={{ border: "1px solid black", padding: "8px" }}>
//                 <strong>Minimum Thickness</strong>

//                 <table
//                   style={{
//                     width: "100%",
//                     borderCollapse: "collapse",
//                     marginTop: "4px",
//                   }}
//                 >
//                   <tbody>
//                     {(() => {
//                       const dc = item?.minimumThickness || {};

//                       return (
//                         <>
//                           {/* Static Fields */}
//                           <tr>
//                             <td>Main frame </td>
//                             <td>{dc?.mainFrame || "--"}</td>
//                           </tr>

//                           <tr>
//                             <td>CR Section</td>
//                             <td>{dc?.crSections || "--"}</td>
//                           </tr>
//                           {Object.entries(dc)
//                             .filter(([key]) =>
//                               key.startsWith("minimumThickness")
//                             )
//                             .sort(
//                               (a, b) =>
//                                 Number(a[0].replace("minimumThickness", "")) -
//                                 Number(b[0].replace("minimumThickness", ""))
//                             )
//                             .map(([key, value], index) => (
//                               <tr key={key}>
//                                 <td>
//                                   {key.replace(
//                                     "minimumThickness",
//                                     "minimum Thickness"
//                                   )}
//                                 </td>
//                                 <td>{value || "--"}</td>
//                               </tr>
//                             ))}
//                         </>
//                       );
//                     })()}
//                   </tbody>
//                 </table>
//               </div>
//             </Col>
//           </>
//         ))}
//       </Row>
//       <Row>
//         <div className="compact-component-table mt-5 mb-6 ">
//           {Object.keys(buildingData)
//             .sort()
//             .map((buildingNum) => {
//               const buildingIndex = Number(buildingNum);
//               const areaSqm = getIndividualBuildingArea(buildingIndex);
//               const areaSqft = getIndividualBuildingAreaSqft(buildingIndex);
//               const totalWeight = getIndividualBuildingWeight(buildingNum);
//               const kgPerSqft = getIndividualKgPerSqft(
//                 buildingIndex,
//                 buildingNum
//               );

//               // 🔹 Material Group Summary
//               const groupSummary = getGroupWiseSummaryByBuilding(
//                 componentBlocks,
//                 buildingNum
//               );

//               let materialTotalWeight = 0;
//               Object.values(groupSummary).forEach((g) => {
//                 if (g.totalWeight > 0) {
//                   materialTotalWeight += g.totalWeight;
//                 }
//               });

//               const cellBorderStyle = "1px solid #e0e0e0";

//               return (
//                 <div
//                   key={buildingNum}
//                   className="building-section mb-5"
//                   style={{ breakInside: "avoid" }}
//                 >
//                   {/* ================= HEADER ================= */}
//                   <h3 style={{ marginBottom: "15px" }}>
//                     Building {buildingNum}
//                   </h3>

//                   {/* ================= MAIN COMPONENT TABLE ================= */}
//                   <table
//                     style={{
//                       width: "100%",
//                       borderCollapse: "collapse",
//                       marginBottom: "20px",
//                       border: cellBorderStyle,
//                     }}
//                   >
//                     <thead>
//                       <tr style={{ backgroundColor: "#f9f9f9" }}>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           SL
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Description
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Total Weight
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Unit
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Total Price
//                         </th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {targetComponents?.map(
//                         (componentType, index) =>
//                           buildingData[buildingNum][componentType] && (
//                             <tr key={componentType}>
//                               <td
//                                 style={{
//                                   padding: "8px",
//                                   border: cellBorderStyle,
//                                 }}
//                               >
//                                 {index + 1}
//                               </td>
//                               <td
//                                 style={{
//                                   padding: "8px",
//                                   border: cellBorderStyle,
//                                 }}
//                               >
//                                 {componentLabels[componentType]}
//                               </td>
//                               <td
//                                 style={{
//                                   padding: "8px",
//                                   border: cellBorderStyle,
//                                 }}
//                               >
//                                 {buildingData[buildingNum][
//                                   componentType
//                                 ].totalWeight.toFixed(3)}
//                               </td>
//                               <td
//                                 style={{
//                                   padding: "8px",
//                                   border: cellBorderStyle,
//                                 }}
//                               >
//                                 KG
//                               </td>
//                               <td
//                                 style={{
//                                   padding: "8px",
//                                   border: cellBorderStyle,
//                                 }}
//                               >
//                                 {buildingData[buildingNum][
//                                   componentType
//                                 ].totalPrice.toFixed(3)}
//                               </td>
//                             </tr>
//                           )
//                       )}
//                     </tbody>
//                   </table>

//                   {/* ================= SUMMARY TABLE (EXISTING) ================= */}
//                   <table
//                     style={{
//                       width: "100%",
//                       borderCollapse: "collapse",
//                       border: cellBorderStyle,
//                       marginBottom: "25px",
//                     }}
//                   >
//                     <tbody>
//                       <tr>
//                         <td
//                           style={{
//                             padding: "12px",
//                             border: cellBorderStyle,
//                             fontWeight: "bold",
//                           }}
//                         >
//                           Total Area
//                         </td>
//                         <td
//                           style={{
//                             padding: "12px",
//                             border: cellBorderStyle,
//                             textAlign: "right",
//                           }}
//                         >
//                           <div>{formatNumber(areaSqm, 3)}</div>
//                           <div>{formatNumber(areaSqft, 3)}</div>
//                         </td>
//                         <td
//                           style={{ padding: "12px", border: cellBorderStyle }}
//                         >
//                           <div>Sqm</div>
//                           <div>Sqft</div>
//                         </td>
//                       </tr>

//                       <tr>
//                         <td
//                           style={{
//                             padding: "12px",
//                             border: cellBorderStyle,
//                             fontWeight: "bold",
//                           }}
//                         >
//                           Total Weight
//                         </td>
//                         <td
//                           style={{
//                             padding: "12px",
//                             border: cellBorderStyle,
//                             textAlign: "right",
//                           }}
//                         >
//                           <div>{formatNumber(totalWeight, 3)}</div>
//                           <div>{formatNumber(totalWeight / 1000, 3)}</div>
//                         </td>
//                         <td
//                           style={{ padding: "12px", border: cellBorderStyle }}
//                         >
//                           <div>Kg</div>
//                           <div>Tonns</div>
//                         </td>
//                       </tr>

//                       <tr>
//                         <td
//                           style={{
//                             padding: "12px",
//                             border: cellBorderStyle,
//                             fontWeight: "bold",
//                           }}
//                         >
//                           Kg per Sqft
//                         </td>
//                         <td
//                           style={{
//                             padding: "12px",
//                             border: cellBorderStyle,
//                             textAlign: "right",
//                           }}
//                         >
//                           {formatNumber(kgPerSqft, 3)}
//                         </td>
//                         <td
//                           style={{ padding: "12px", border: cellBorderStyle }}
//                         >
//                           Kg/Sqft
//                         </td>
//                       </tr>
//                     </tbody>
//                   </table>

//                   {/* ================= MATERIAL GROUP SUMMARY (NEW) ================= */}
//                   <h4 style={{ marginBottom: "10px" }}>
//                     Building {buildingNum} – Material Group Summary
//                   </h4>

//                   <table
//                     style={{
//                       width: "100%",
//                       borderCollapse: "collapse",
//                       border: cellBorderStyle,
//                     }}
//                   >
//                     <thead>
//                       <tr style={{ backgroundColor: "#f9f9f9" }}>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           SL
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Material Group
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Weight (Kg)
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Area (Sqft)
//                         </th>
//                         <th
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           Kg / Sqft
//                         </th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {Object.keys(groupSummary).map((groupKey, index) => {
//                         const g = groupSummary[groupKey];
//                         if (g.totalWeight === 0) return null;

//                         return (
//                           <tr key={groupKey}>
//                             <td
//                               style={{
//                                 padding: "8px",
//                                 border: cellBorderStyle,
//                               }}
//                             >
//                               {index + 1}
//                             </td>
//                             <td
//                               style={{
//                                 padding: "8px",
//                                 border: cellBorderStyle,
//                               }}
//                             >
//                               {g.label}
//                             </td>
//                             <td
//                               style={{
//                                 padding: "8px",
//                                 border: cellBorderStyle,
//                               }}
//                             >
//                               {g.totalWeight.toFixed(3)}
//                             </td>
//                             <td
//                               style={{
//                                 padding: "8px",
//                                 border: cellBorderStyle,
//                               }}
//                             >
//                               {g.areaSft.toFixed(3)}
//                             </td>
//                             <td
//                               style={{
//                                 padding: "8px",
//                                 border: cellBorderStyle,
//                               }}
//                             >
//                               {g.kgPerSft.toFixed(3)}
//                             </td>
//                           </tr>
//                         );
//                       })}

//                       {/* TOTAL */}
//                       <tr style={{ fontWeight: "bold", background: "#fafafa" }}>
//                         <td
//                           colSpan={2}
//                           style={{
//                             padding: "10px",
//                             border: cellBorderStyle,
//                             textAlign: "right",
//                           }}
//                         >
//                           Total
//                         </td>
//                         <td
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           {materialTotalWeight.toFixed(3)}
//                         </td>
//                         <td
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           {areaSqft.toFixed(3)}
//                         </td>
//                         <td
//                           style={{ padding: "10px", border: cellBorderStyle }}
//                         >
//                           {(materialTotalWeight / areaSqft).toFixed(3)}
//                         </td>
//                       </tr>
//                     </tbody>
//                   </table>

//                   {/* ================= MATERIAL DETAILED BREAKUP (ADDED – NO DISTURB) ================= */}
//                   <h4 style={{ marginTop: "30px", marginBottom: "10px" }}>
//                     Building {buildingNum} – Material Detailed Breakup
//                   </h4>

//                   {Object.entries(
//                     getGroupItemsForBuilding(componentBlocks, buildingNum)
//                   ).map(([groupKey, items]) => {
//                     if (!items || !items.length) return null;

//                     const breakup = getMaterialBreakupByGroup(groupKey, items);

//                     return (
//                       <table
//                         key={groupKey}
//                         style={{
//                           width: "100%",
//                           borderCollapse: "collapse",
//                           border: "1px solid #e0e0e0",
//                           marginBottom: "25px",
//                         }}
//                       >
//                         <thead>
//                           <tr style={{ background: "#f3f3f3" }}>
//                             <th
//                               colSpan={4}
//                               style={{
//                                 textAlign: "left",
//                                 padding: "10px",
//                                 border: "1px solid #e0e0e0",
//                               }}
//                             >
//                               {MATERIAL_GROUPS[groupKey]?.label || groupKey}
//                             </th>
//                           </tr>
//                           <tr style={{ background: "#fafafa" }}>
//                             <th
//                               style={{
//                                 border: "1px solid #e0e0e0",
//                                 padding: "8px",
//                               }}
//                             >
//                               Spec
//                             </th>
//                             <th
//                               style={{
//                                 border: "1px solid #e0e0e0",
//                                 padding: "8px",
//                               }}
//                             >
//                               Total Area of Each Material
//                             </th>
//                             <th
//                               style={{
//                                 border: "1px solid #e0e0e0",
//                                 padding: "8px",
//                               }}
//                             >
//                               Weight
//                             </th>
//                             <th
//                               style={{
//                                 border: "1px solid #e0e0e0",
//                                 padding: "8px",
//                               }}
//                             >
//                               Kg / Sqft
//                             </th>
//                           </tr>
//                         </thead>

//                         <tbody>
//                           {Object.entries(breakup).flatMap(([key, value]) => {
//                             const renderRow = (label, d, idx) => (
//                               <tr key={`${label}-${idx}`}>
//                                 <td
//                                   style={{
//                                     border: "1px solid #e0e0e0",
//                                     padding: "8px",
//                                   }}
//                                 >
//                                   {label}
//                                 </td>
//                                 <td
//                                   style={{
//                                     border: "1px solid #e0e0e0",
//                                     padding: "8px",
//                                     textAlign: "center",
//                                   }}
//                                 >
//                                   {/* Dynamic Unit: Area unte d.unit (SQM) chupisthundi, lekapothe thickness */}
//                                   {d.area > 0
//                                     ? `${formatNumber(d.area, 3)} ${
//                                         d.unit || "Sqm"
//                                       }`
//                                     : d.thickness || "-"}
//                                 </td>
//                                 <td
//                                   style={{
//                                     border: "1px solid #e0e0e0",
//                                     padding: "8px",
//                                   }}
//                                 >
//                                   {/* Weight unte KG, lekapothe Quantity + original Unit (Nos/Mtr/etc) */}
//                                   {d.weight > 0
//                                     ? `${formatNumber(d.weight, 3)} Kg`
//                                     : `${d.quantity || 0} ${d.unit || "Nos"}`}
//                                 </td>
//                                 <td
//                                   style={{
//                                     border: "1px solid #e0e0e0",
//                                     padding: "8px",
//                                   }}
//                                 >
//                                   {d.weight > 0
//                                     ? formatNumber(
//                                         d.weight /
//                                           getIndividualBuildingAreaSqft(
//                                             buildingNum
//                                           ),
//                                         3
//                                       )
//                                     : "-"}
//                                 </td>
//                               </tr>
//                             );

//                             if (
//                               value &&
//                               typeof value === "object" &&
//                               value.area === undefined
//                             ) {
//                               return Object.entries(value).map(([spec, d], i) =>
//                                 renderRow(spec, d, i)
//                               );
//                             }

//                             return renderRow(key, value, 0);
//                           })}
//                         </tbody>
//                       </table>
//                     );
//                   })}
//                   {/* ================= BUILDING ANALYSIS (ADDED) ================= */}
//                   <h4 style={{ marginTop: "30px", marginBottom: "10px" }}>
//                     Building {buildingNum} - Analysis
//                   </h4>

//                   {(() => {
//                     const areaSqft =
//                       getIndividualBuildingAreaSqft(Number(buildingNum)) || 1;

//                     const mySelectedComponents = [
//                       "leanTo",
//                       "canopy",
//                       "frameOpening",
//                       "partitionWall",
//                       "cageLadder",
//                       "craneBeam",
//                       "mezzanine",
//                       "fascia",
//                       "structuralLouver",
//                       "staircase",
//                     ];

//                     const activeComponents = mySelectedComponents.filter(
//                       (compKey) => {
//                         const compWeight =
//                           buildingData?.[buildingNum]?.[compKey]?.totalWeight;
//                         return Number(compWeight) > 0;
//                       }
//                     );

//                     if (activeComponents.length === 0) return null;

//                     return (
//                       <div className="mb-12 p-4 border rounded shadow-sm bg-white">
//                         {activeComponents.map((componentType) => {
//   const items = data?.componentBlocks?.[componentType]?.[buildingNum];
//   if (!items || items.length === 0) return null;

//   // --- LOGIC 1: AGGREGATE FOR SUMMARY TABLE ---
//   const groupSummaries = {};
//   const detailedGrouped = {};

//   items.forEach((item) => {
//     const code = item.materialCode || item.code || "";
//     let matchedGroup = "OTHERS";

//     Object.entries(MATERIAL_GROUPS).forEach(([groupKey, config]) => {
//       if (config.match(code)) matchedGroup = groupKey;
//     });

//     // 1. Accumulate for Summary
//     if (!groupSummaries[matchedGroup]) {
//       groupSummaries[matchedGroup] = {
//         label: MATERIAL_GROUPS[matchedGroup]?.label || matchedGroup,
//         totalWeight: 0,
//         totalArea: 0,
//       };
//     }
//     const weight = Number(item.totalWeight) || 0;
//     const pm = Number(item.pieceMeasure) || 0;
//     const qty = Number(item.quantity) || 0;
//     const avg = Number(item.avg) > 0 ? Number(item.avg) : 1;

//     groupSummaries[matchedGroup].totalWeight += weight;
//     groupSummaries[matchedGroup].totalArea += (pm * qty * avg);

//     // 2. Organize for Detailed List
//     if (!detailedGrouped[matchedGroup]) detailedGrouped[matchedGroup] = [];
//     detailedGrouped[matchedGroup].push(item);
//   });

//   return (
//     <div key={componentType} className="mt-8 p-4 border rounded shadow-sm bg-white">
//       {/* COMPONENT HEADER */}
//       <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
//         <h4 className="font-bold text-primary text-uppercase mb-0">
//           {componentLabels[componentType] || componentType} - Analysis
//         </h4>
//         <span className="badge badge-dark p-2">B{buildingNum}</span>
//       </div>

//       {/* --- TABLE A: GROUP-WISE SUMMARY  --- */}
//       <div className="mb-6">
//         <h6 className="font-bold text-secondary mb-2">I. Material Group Summary</h6>
//         <table className="table table-sm table-bordered shadow-sm">
//           <thead className="bg-dark text-white small text-center">
//             <tr>
//               <th className="text-left">Material Category</th>
//               <th>Total Weight (Kg)</th>
//               <th>Kg / Sqft</th>
//             </tr>
//           </thead>
//           <tbody className="small">
//             {Object.entries(groupSummaries).map(([key, g]) => (
//               <tr key={key} className="text-center font-weight-bold">
//                 <td className="text-left bg-light">{g.label}</td>
//                 <td>{g.totalWeight.toFixed(3)}</td>
//                 <td className="text-primary">{(g.totalWeight / areaSqft).toFixed(3)}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* --- TABLE B: MATERIAL-WISE DETAILS (KINDHA) --- */}
//       <div>
//         <h6 className="font-bold text-secondary mb-2">II. Detailed Material Breakdown</h6>
//         {Object.entries(detailedGrouped).map(([groupKey, mItems]) => (
//           <div key={groupKey} className="mb-4">
//             <div className="bg-light p-2 border font-weight-bold small text-secondary">
//               {MATERIAL_GROUPS[groupKey]?.label || groupKey}
//             </div>
//             <table className="table table-sm table-bordered bg-white mb-0">
//               <thead className="small text-center bg-white text-muted">
//                 <tr>
//                   <th className="text-left">Description</th>
//                   <th>Code</th>
//                   <th>Weight</th>
//                   <th>Kg/Sqft</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {mItems.map((item, idx) => {
//                   const w = Number(item.totalWeight || 0);
//                   if (w <= 0 && Number(item.quantity || 0) <= 0) return null;
//                   return (
//                     <tr key={idx} className="small text-center">
//                       <td className="text-left p-2 border">{item.materialDescription || item.description}</td>
//                       <td className="p-2 border">{item.materialCode || item.code || "-"}</td>
//                       <td className="p-2 border font-weight-bold">{w.toFixed(3)}</td>
//                       <td className="p-2 border text-info">{(w / areaSqft).toFixed(3)}</td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// })}
//                       </div>
//                     );
//                   })()}
//                   {/* ================= BUILDING REMARKS ================= */}
//                   {(() => {
//                     const buildingRemarks = data?.buildings?.find(
//                       (b) => String(b.buildingId) === String(buildingNum)
//                     );

//                     if (!buildingRemarks?.remarksEstimationCreation)
//                       return null;

//                     return (
//                       <div
//                         style={{
//                           border: "1px solid black",
//                           padding: "8px",
//                           marginTop: "20px",
//                         }}
//                       >
//                         <strong>
//                           Remarks: Building {buildingRemarks.buildingId}{" "}
//                           {buildingRemarks.buildingTypes}
//                         </strong>

//                         <div
//                           style={{ marginTop: "6px", paddingLeft: "15px" }}
//                           dangerouslySetInnerHTML={{
//                             __html:
//                               buildingRemarks.remarksEstimationCreation || "",
//                           }}
//                         />
//                       </div>
//                     );
//                   })()}
//                 </div>
//               );
//             })}
//         </div>
//       </Row>

//       {/* for component wise */}

//       {/* for all buildings caluclations */}
//       <div className="compact-component-table mt-5">
//         <div className="building-section">
//           <h3>
//             Combined Total Estimation for All Buildings ({totalBuildings}{" "}
//             Buildings)
//           </h3>
//           <table
//             border="1"
//             style={{
//               borderCollapse: "collapse",
//               width: "100%",
//               marginBottom: "20px",
//             }}
//           >
//             <thead>
//               <tr style={{ backgroundColor: "#f5f5f5" }}>
//                 <th style={{ padding: "8px", textAlign: "left" }}>SL</th>
//                 <th style={{ padding: "8px", textAlign: "left" }}>
//                   Description
//                 </th>
//                 <th style={{ padding: "8px", textAlign: "left" }}>
//                   Total Weight
//                 </th>
//                 <th style={{ padding: "8px", textAlign: "left" }}>Unit</th>
//                 {/* <th style={{ padding: "8px", textAlign: "left" }}>Unit Price</th> */}
//                 <th style={{ padding: "8px", textAlign: "left" }}>
//                   Total Price
//                 </th>
//               </tr>
//             </thead>
//             <tbody>
//               {combinedData.map((component, index) => (
//                 <tr key={index}>
//                   <td style={{ padding: "8px" }}>{index + 1}</td>
//                   <td style={{ padding: "8px" }}>{component.description}</td>
//                   <td style={{ padding: "8px" }}>{component.totalWeight}</td>
//                   <td style={{ padding: "8px" }}>KG</td>
//                   {/* <td style={{ padding: "8px" }}>{component.unitPrice}</td> */}
//                   <td style={{ padding: "8px" }}>{component.totalPrice}</td>
//                 </tr>
//               ))}
//             </tbody>
//             <tfoot>
//               <tr style={{ backgroundColor: "#e0f7fa" }}>
//                 <td
//                   colSpan="2"
//                   style={{
//                     padding: "8px",
//                     fontWeight: "bold",
//                     textAlign: "right",
//                   }}
//                 >
//                   Grand Total:
//                 </td>
//                 <td style={{ padding: "8px", fontWeight: "bold" }}>
//                   {combinedGrandTotalWeight}
//                 </td>
//                 <td style={{ padding: "8px" }}>KG</td>
//                 {/* <td style={{ padding: "8px" }}>-</td> */}
//                 <td style={{ padding: "8px", fontWeight: "bold" }}>
//                   {combinedGrandTotalPrice}
//                 </td>
//               </tr>
//             </tfoot>
//           </table>
//         </div>
//       </div>

//       <Row className="mb-3">
//         <Col>
//           <table style={{ width: "100%", borderCollapse: "collapse" }}>
//             <tbody>
//               <tr>
//                 <td style={{ padding: "4px", width: "50%" }}>
//                   <strong>Total Buildings in Estimation:</strong>
//                 </td>
//                 <td
//                   className="text-right"
//                   style={{ padding: "4px", fontWeight: "bold" }}
//                 >
//                   {totalBuildings}
//                 </td>
//                 <td></td>
//               </tr>
//               <tr>
//                 <td style={{ padding: "4px", width: "50%" }}>
//                   <strong>Total Area:</strong>
//                 </td>
//                 <td
//                   className="d-flex flex-column justify-content-end"
//                   style={{ padding: "4px" }}
//                 >
//                   <tr className="text-right">{totalBuildingArea.toFixed(3)}</tr>
//                   <tr className="text-right">
//                     {(totalBuildingArea * 10.764).toFixed(3)}
//                   </tr>
//                 </td>
//                 <td>
//                   <tr>Sqm</tr>
//                   <tr>Sqft</tr>
//                 </td>
//               </tr>
//               <tr>
//                 <td style={{ padding: "4px" }}>
//                   <strong>Total Weight:</strong>
//                 </td>
//                 <td
//                   className="d-flex flex-column justify-content-end"
//                   style={{ padding: "4px" }}
//                 >
//                   <tr className="text-right">{grandTotalWeight.toFixed(3)}</tr>
//                   <tr className="text-right">
//                     {(grandTotalWeight / 1000).toFixed(3)}
//                   </tr>
//                 </td>
//                 <td>
//                   <tr>Kg</tr>
//                   <tr>Tonns</tr>
//                 </td>
//               </tr>
//               <tr>
//                 <td style={{ padding: "4px" }}>
//                   <strong>kg per sft:</strong>
//                 </td>
//                 <td className="text-right" style={{ padding: "4px" }}>
//                   {(grandTotalWeight / (totalBuildingArea * 10.764)).toFixed(3)}
//                 </td>
//                 <td>Kg/Sqft</td>
//               </tr>
//             </tbody>
//           </table>
//         </Col>
//       </Row>

//       <div className="compact-component-table mt-5">
//         <div className="building-section">
//           <h3>
//             Combined Material-wise Summary (All {totalBuildings} Buildings)
//           </h3>

//           <table
//             border="1"
//             style={{
//               borderCollapse: "collapse",
//               width: "100%",
//               marginBottom: "20px",
//             }}
//           >
//             <thead>
//               <tr style={{ backgroundColor: "#f5f5f5" }}>
//                 <th style={{ padding: "8px" }}>SL</th>
//                 <th style={{ padding: "8px" }}>Material Description</th>
//                 <th style={{ padding: "8px" }}>Material Code</th>
//                 <th style={{ padding: "8px" }}>Total Weight (Kg)</th>
//                 <th style={{ padding: "8px" }}>Kg / Sqft</th>
//               </tr>
//             </thead>

//             {/* <tbody>
//               {combinedMaterialList.map((mat, index) => (
//                 <tr key={index}>
//                   <td style={{ padding: "8px" }}>{index + 1}</td>
//                   <td style={{ padding: "8px" }}>{mat.description}</td>
//                   <td style={{ padding: "8px" }}>{mat.materialCode}</td>
//                   <td style={{ padding: "8px", fontWeight: "bold" }}>
//                     {mat.totalWeight.toFixed(3)}
//                   </td>
//                   <td style={{ padding: "8px" }}>
//                     {(mat.totalWeight / totalSqftAllBuildings).toFixed(3)}
//                   </td>
//                 </tr>
//               ))}
//             </tbody> */}
//             <tbody>
//   {groupedMaterialList.map((mat, index) => (
//     <tr key={mat.materialCode}>
//       <td style={{ padding: "8px" }}>{index + 1}</td>
//       {/* Showing just the Material Code as Description since it's combined */}
//       <td style={{ padding: "8px" }}>{mat.materialCode} Summary</td>
//       <td style={{ padding: "8px" }}>{mat.materialCode}</td>
//       <td style={{ padding: "8px", fontWeight: "bold" }}>
//         {mat.totalWeight.toFixed(3)}
//       </td>
//       <td style={{ padding: "8px" }}>
//         {(mat.totalWeight / totalSqftAllBuildings).toFixed(3)}
//       </td>
//     </tr>
//   ))}
// </tbody>

//             <tfoot>
//               <tr style={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}>
//                 <td colSpan="3" style={{ textAlign: "right", padding: "8px" }}>
//                   Grand Total
//                 </td>
//                 <td style={{ padding: "8px" }}>
//                   {grandTotalWeight.toFixed(3)}
//                 </td>
//                 <td style={{ padding: "8px" }}>
//                   {(grandTotalWeight / totalSqftAllBuildings).toFixed(3)}
//                 </td>
//               </tr>
//             </tfoot>
//           </table>
//         </div>
//       </div>

//       {/* Remarks Section */}

//       <Row>
//         {data?.buildings?.map((item) => (
//           <Col md={12} className="mb-2">
//             <div
//               style={{
//                 border: "1px solid black",
//                 padding: "8px",
//                 height: "100%",
//               }}
//             >
//               <strong>
//                 Remarks: Building {item?.buildingId} {item?.buildingTypes}
//               </strong>
//               <ol style={{ margin: "4px 0", paddingLeft: "20px" }}>
//                 {/* <RichTextEditor
//                                                           value={
//                                                             item?.remarksEstimationCreation ||
//                                                             ""
//                                                           }
//                                                            readOnly={true}
//    modules={{ toolbar: false }}

                                                         
//                                                         /> */}
//                 <div
//                   dangerouslySetInnerHTML={{
//                     __html: item?.remarksEstimationCreation || "",
//                   }}
//                 ></div>

//                 {/* {item?.remarksEstimationCreation} */}
//               </ol>
//             </div>
//           </Col>
//         ))}
//       </Row>

//       {/* Status Section */}
//       <Row className="mt-3">
//         <Col>
//           <div
//             style={{
//               textAlign: "center",
//               padding: "8px",
//               border: "1px solid black",
//             }}
//           >
//             <strong>Estimation Status:</strong> {data?.estimationStatus} |
//             <strong> Header Status:</strong> {data?.headerStatus} |
//             <strong> Version:</strong> {data?.version}
//           </div>
//         </Col>
//       </Row>
//     </div>
//   );

return (
    <div ref={ref} style={printbody} className="m-4">
      {/* Header Section - always print */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <img
              src="/assets/img/HB/halleysblue.jpeg"
              alt="HalleysBlue Steels"
              className="headerLogo"
              style={{ height: "60px" }}
            />
            <div className="text-center">
              <h4 className="mb-0">HalleysBlue Steels Pvt Ltd</h4>
              <small>Pre Engineered Buildings</small>
            </div>
            <div style={{ width: "100px" }}></div>
          </div>
        </Col>
      </Row>

      <hr className="my-2" />

      {/* Document Details - always print */}
      <Row className="mb-3">
        <Col className="d-flex justify-content-center">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ padding: "2px 0" }}>
                  <span className="px-2">
                    {" "}
                    <strong>Client Name:</strong> {data?.clientName}
                  </span>
                </td>
                <td style={{ padding: "2px 0", textAlign: "right" }}>
                  <span className="px-2">
                    <strong className="px-2">Date:</strong>{" "}
                    {formatDate(data?.createdAt)}
                  </span>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "2px 0" }}>
                  <span className="px-2">
                    {" "}
                    <strong>Description:</strong> Estimate for the proposed PEB
                    @{data?.location}
                  </span>
                </td>
                <td style={{ padding: "2px 0", textAlign: "right" }}>
                  <span className="px-2">
                    {" "}
                    <strong>Rev:</strong> {data?.version || "v0"}
                  </span>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "2px 0" }}></td>
                <td style={{ padding: "2px 0", textAlign: "right" }}>
                  <span className="px-2">
                    <strong>Prep By:</strong> {data?.designerName}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
      </Row>

      {/* ✅ BUILDING SPECS WRAP */}
      {show("buildingSpecs") && (
        <Row>
          {data?.buildings?.map((item) => (
            <>
              <Col md={12} className="mb-2">
                <div
                  style={{
                    border: "1px solid black",
                    padding: "8px",
                    height: "100%",
                  }}
                >
                  <strong>Building Specifications : {item?.buildingTypes}</strong>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "4px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>Width O/O</td>
                        <td>
                          {item?.front?.meters} m {""}{" "}
                        </td>
                      </tr>
                      <tr>
                        <td></td>
                        <td>Bay Spacing</td>
                        <td>
                          {baySpacingSummary(item?.front?.rows)} m {""}
                        </td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>Length O/O</td>
                        <td>
                          {item?.left?.meters} m {""}{" "}
                        </td>
                      </tr>
                      <tr>
                        <td></td>
                        <td>Bay Spacing</td>
                        <td>
                          {item?.left?.bays}@{item?.left?.rows?.[0]?.baySpacing} m{" "}
                          {""}
                        </td>
                      </tr>
                      <tr>
                        <td>3</td>
                        <td>Eave height</td>
                        <td>
                          {item?.selectedCaseObject?.eaveHeightLSW && (
                            <>
                              LSW : {item?.selectedCaseObject?.eaveHeightLSW} m{" "}
                            </>
                          )}
                          {item?.selectedCaseObject?.eaveHeightRSW && (
                            <>RSW : {item?.selectedCaseObject?.eaveHeightRSW} m</>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>No of interior MS</td>
                        <td>
                          {item?.mainBuildingTypeCode === "MSB" ||
                          item?.subBuildingTypeCode === "MSB" ? (
                            <span>{formatHeights(item?.selectedCaseObject)}</span>
                          ) : (
                            <span>Nill</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>Roof Bracings</td>
                        <td>{item?.roofBracing}</td>
                      </tr>
                      <tr>
                        <td>5</td>
                        <td>Side Bracing</td>
                        <td>{item?.sideBracing}</td>
                      </tr>
                      <tr>
                        <td>7</td>
                        <td>Open Wall / Masonry Conditions</td>
                        <td>{item?.openWallMasonaryConditions}</td>
                      </tr>
                      <tr>
                        <td>8</td>
                        <td>Ridge Height (Slope)</td>
                        <td>
                          {item?.selectedCaseObject?.ridgeHeight || "--"} m {""}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Col>
              <Col>
                <div
                  style={{
                    border: "1px solid black",
                    padding: "8px",
                    height: "100%",
                  }}
                >
                  <strong>Loading Details</strong>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "4px",
                    }}
                  >
                    <tbody>
                      {(() => {
                        const load = item?.designLoad || {};
                        return (
                          <>
                            <tr>
                              <td>Dead Load (kn/Sq.m)</td>
                              <td>{load?.deadLoad || "--"}</td>
                            </tr>
                            <tr>
                              <td>Live Load (kn/Sq.m)</td>
                              <td>{load?.liveLoad || "--"}</td>
                            </tr>
                            <tr>
                              <td>Solar Load (kn/Sq.m)</td>
                              <td>{load?.solarLoad || "--"}</td>
                            </tr>
                            <tr>
                              <td>Wind Load (kmph)</td>
                              <td>{load?.windLoad || "--"}</td>
                            </tr>
                            {Object.entries(load)
                              .filter(([key]) => key.startsWith("designLoad"))
                              .sort(
                                (a, b) =>
                                  Number(a[0].replace("designLoad", "")) -
                                  Number(b[0].replace("designLoad", ""))
                              )
                              .map(([key, value], i) => (
                                <tr key={i}>
                                  <td>
                                    {key.replace("designLoad", "Design Load ")}
                                  </td>
                                  <td>{value || "--"}</td>
                                </tr>
                              ))}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </Col>
              <Col md={12} className="mb-2">
                <div style={{ border: "1px solid black", padding: "8px" }}>
                  <strong>Design Standards</strong>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "4px",
                    }}
                  >
                    <tbody>
                      {(() => {
                        const ds = item?.designStandards || {};
                        return (
                          <>
                            <tr>
                              <td>1</td>
                              <td>Codes Applicable</td>
                              <td>{ds?.codesApplicable || "--"}</td>
                            </tr>
                            <tr>
                              <td>2</td>
                              <td>Wind Load Calculation</td>
                              <td>{ds?.windLoadCalculation || "--"}</td>
                            </tr>
                            <tr>
                              <td>3</td>
                              <td>Wind Load Application</td>
                              <td>{ds?.windLoadApplication || "--"}</td>
                            </tr>
                            <tr>
                              <td>4</td>
                              <td>Seismic Load</td>
                              <td>{ds?.seismicLoad || "--"}</td>
                            </tr>
                            {Object.entries(ds)
                              .filter(([key]) => key.startsWith("designStandard"))
                              .sort(
                                (a, b) =>
                                  Number(a[0].replace("designStandard", "")) -
                                  Number(b[0].replace("designStandard", ""))
                              )
                              .map(([key, value], index) => (
                                <tr key={key}>
                                  <td>{index + 5}</td>
                                  <td>
                                    {key.replace("designStandard", "Design Standard ")}
                                  </td>
                                  <td>{value || "--"}</td>
                                </tr>
                              ))}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </Col>
              <Col md={12}>
                <div style={{ border: "1px solid black", padding: "8px" }}>
                  <strong>Deflection Criteria</strong>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "4px",
                    }}
                  >
                    <tbody>
                      {(() => {
                        const dc = item?.deflectionCriteria || {};
                        return (
                          <>
                            <tr>
                              <td>Main frame (Sway)</td>
                              <td>{dc?.mainFramesSway || "--"}</td>
                            </tr>
                            <tr>
                              <td>Main frame (Deflection)</td>
                              <td>{dc?.mainFrameDeflection || "--"}</td>
                            </tr>
                            {Object.entries(dc)
                              .filter(([key]) => key.startsWith("deflectionCriteria"))
                              .sort(
                                (a, b) =>
                                  Number(a[0].replace("deflectionCriteria", "")) -
                                  Number(b[0].replace("deflectionCriteria", ""))
                              )
                              .map(([key, value], index) => (
                                <tr key={key}>
                                  <td>
                                    {key.replace("deflectionCriteria", "Deflection Criteria")}
                                  </td>
                                  <td>{value || "--"}</td>
                                </tr>
                              ))}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </Col>
              <Col md={12}>
                <div style={{ border: "1px solid black", padding: "8px" }}>
                  <strong>Minimum Thickness</strong>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "4px",
                    }}
                  >
                    <tbody>
                      {(() => {
                        const dc = item?.minimumThickness || {};
                        return (
                          <>
                            <tr>
                              <td>Main frame </td>
                              <td>{dc?.mainFrame || "--"}</td>
                            </tr>
                            <tr>
                              <td>CR Section</td>
                              <td>{dc?.crSections || "--"}</td>
                            </tr>
                            {Object.entries(dc)
                              .filter(([key]) => key.startsWith("minimumThickness"))
                              .sort(
                                (a, b) =>
                                  Number(a[0].replace("minimumThickness", "")) -
                                  Number(b[0].replace("minimumThickness", ""))
                              )
                              .map(([key, value], index) => (
                                <tr key={key}>
                                  <td>
                                    {key.replace("minimumThickness", "minimum Thickness")}
                                  </td>
                                  <td>{value || "--"}</td>
                                </tr>
                              ))}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </Col>
            </>
          ))}
        </Row>
      )}

      {/* ✅ PER-BUILDING SECTIONS */}
      <Row>
        <div className="compact-component-table mt-5 mb-6 ">
          {Object.keys(buildingData)
            .sort()
            .map((buildingNum) => {
              const buildingIndex = Number(buildingNum);
              const areaSqm = getIndividualBuildingArea(buildingIndex);
              const areaSqft = getIndividualBuildingAreaSqft(buildingIndex);
              const totalWeight = getIndividualBuildingWeight(buildingNum);
              const kgPerSqft = getIndividualKgPerSqft(buildingIndex, buildingNum);

              const groupSummary = getGroupWiseSummaryByBuilding(
                componentBlocks,
                buildingNum
              );

              let materialTotalWeight = 0;
              Object.values(groupSummary).forEach((g) => {
                if (g.totalWeight > 0) {
                  materialTotalWeight += g.totalWeight;
                }
              });

              const cellBorderStyle = "1px solid #e0e0e0";

              return (
                <div
                  key={buildingNum}
                  className="building-section mb-5"
                  style={{ breakInside: "avoid" }}
                >
                  <h3 style={{ marginBottom: "15px" }}>
                    Building {buildingNum}
                  </h3>

                  {/* ✅ COMPONENT TABLE + SUMMARY WRAP */}
                  {show("buildingComponents") && (
                    <>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          marginBottom: "20px",
                          border: cellBorderStyle,
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#f9f9f9" }}>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>SL</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Description</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Total Weight</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Unit</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Total Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {targetComponents?.map(
                            (componentType, index) =>
                              buildingData[buildingNum][componentType] && (
                                <tr key={componentType}>
                                  <td style={{ padding: "8px", border: cellBorderStyle }}>{index + 1}</td>
                                  <td style={{ padding: "8px", border: cellBorderStyle }}>{componentLabels[componentType]}</td>
                                  <td style={{ padding: "8px", border: cellBorderStyle }}>
                                    {buildingData[buildingNum][componentType].totalWeight.toFixed(3)}
                                  </td>
                                  <td style={{ padding: "8px", border: cellBorderStyle }}>KG</td>
                                  <td style={{ padding: "8px", border: cellBorderStyle }}>
                                    {buildingData[buildingNum][componentType].totalPrice.toFixed(3)}
                                  </td>
                                </tr>
                              )
                          )}
                        </tbody>
                      </table>

                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          border: cellBorderStyle,
                          marginBottom: "25px",
                        }}
                      >
                        <tbody>
                          <tr>
                            <td style={{ padding: "12px", border: cellBorderStyle, fontWeight: "bold" }}>Total Area</td>
                            <td style={{ padding: "12px", border: cellBorderStyle, textAlign: "right" }}>
                              <div>{formatNumber(areaSqm, 3)}</div>
                              <div>{formatNumber(areaSqft, 3)}</div>
                            </td>
                            <td style={{ padding: "12px", border: cellBorderStyle }}>
                              <div>Sqm</div>
                              <div>Sqft</div>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: "12px", border: cellBorderStyle, fontWeight: "bold" }}>Total Weight</td>
                            <td style={{ padding: "12px", border: cellBorderStyle, textAlign: "right" }}>
                              <div>{formatNumber(totalWeight, 3)}</div>
                              <div>{formatNumber(totalWeight / 1000, 3)}</div>
                            </td>
                            <td style={{ padding: "12px", border: cellBorderStyle }}>
                              <div>Kg</div>
                              <div>Tonns</div>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: "12px", border: cellBorderStyle, fontWeight: "bold" }}>Kg per Sqft</td>
                            <td style={{ padding: "12px", border: cellBorderStyle, textAlign: "right" }}>
                              {formatNumber(kgPerSqft, 3)}
                            </td>
                            <td style={{ padding: "12px", border: cellBorderStyle }}>Kg/Sqft</td>
                          </tr>
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* ✅ MATERIAL GROUP SUMMARY WRAP */}
                  {show("materialGroupSummary") && (
                    <>
                      <h4 style={{ marginBottom: "10px" }}>
                        Building {buildingNum} – Material Group Summary
                      </h4>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          border: cellBorderStyle,
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#f9f9f9" }}>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>SL</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Material Group</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Weight (Kg)</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Area (Sqft)</th>
                            <th style={{ padding: "10px", border: cellBorderStyle }}>Kg / Sqft</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(groupSummary).map((groupKey, index) => {
                            const g = groupSummary[groupKey];
                            if (g.totalWeight === 0) return null;
                            return (
                              <tr key={groupKey}>
                                <td style={{ padding: "8px", border: cellBorderStyle }}>{index + 1}</td>
                                <td style={{ padding: "8px", border: cellBorderStyle }}>{g.label}</td>
                                <td style={{ padding: "8px", border: cellBorderStyle }}>{g.totalWeight.toFixed(3)}</td>
                                <td style={{ padding: "8px", border: cellBorderStyle }}>{g.areaSft.toFixed(3)}</td>
                                <td style={{ padding: "8px", border: cellBorderStyle }}>{g.kgPerSft.toFixed(3)}</td>
                              </tr>
                            );
                          })}
                          <tr style={{ fontWeight: "bold", background: "#fafafa" }}>
                            <td colSpan={2} style={{ padding: "10px", border: cellBorderStyle, textAlign: "right" }}>Total</td>
                            <td style={{ padding: "10px", border: cellBorderStyle }}>{materialTotalWeight.toFixed(3)}</td>
                            <td style={{ padding: "10px", border: cellBorderStyle }}>{areaSqft.toFixed(3)}</td>
                            <td style={{ padding: "10px", border: cellBorderStyle }}>{(materialTotalWeight / areaSqft).toFixed(3)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* ✅ MATERIAL DETAILED BREAKUP WRAP */}
                  {show("materialDetailedBreakup") && (
                    <>
                      <h4 style={{ marginTop: "30px", marginBottom: "10px" }}>
                        Building {buildingNum} – Material Detailed Breakup
                      </h4>
                      {Object.entries(
                        getGroupItemsForBuilding(componentBlocks, buildingNum)
                      ).map(([groupKey, items]) => {
                        if (!items || !items.length) return null;
                        const breakup = getMaterialBreakupByGroup(groupKey, items);
                        return (
                          <table
                            key={groupKey}
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              border: "1px solid #e0e0e0",
                              marginBottom: "25px",
                            }}
                          >
                            <thead>
                              <tr style={{ background: "#f3f3f3" }}>
                                <th colSpan={4} style={{ textAlign: "left", padding: "10px", border: "1px solid #e0e0e0" }}>
                                  {MATERIAL_GROUPS[groupKey]?.label || groupKey}
                                </th>
                              </tr>
                              <tr style={{ background: "#fafafa" }}>
                                <th style={{ border: "1px solid #e0e0e0", padding: "8px" }}>Spec</th>
                                <th style={{ border: "1px solid #e0e0e0", padding: "8px" }}>Total Area of Each Material</th>
                                <th style={{ border: "1px solid #e0e0e0", padding: "8px" }}>Weight</th>
                                <th style={{ border: "1px solid #e0e0e0", padding: "8px" }}>Kg / Sqft</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(breakup).flatMap(([key, value]) => {
                                const renderRow = (label, d, idx) => (
                                  <tr key={`${label}-${idx}`}>
                                    <td style={{ border: "1px solid #e0e0e0", padding: "8px" }}>{label}</td>
                                    <td style={{ border: "1px solid #e0e0e0", padding: "8px", textAlign: "center" }}>
                                      {d.area > 0 ? `${formatNumber(d.area, 3)} ${d.unit || "Sqm"}` :  "-"} 
                                    </td>
                                    
                                    <td style={{ border: "1px solid #e0e0e0", padding: "8px" }}>
                                      {d.weight > 0 ? `${formatNumber(d.weight, 3)} Kg` : `${d.quantity || 0} ${d.unit || "Nos"}`}
                                    </td>
                                    <td style={{ border: "1px solid #e0e0e0", padding: "8px" }}>
                                      {d.weight > 0 ? formatNumber(d.weight / getIndividualBuildingAreaSqft(buildingNum), 3) : "-"}
                                    </td>
                                  </tr>
                                );
                                if (value && typeof value === "object" && value.area === undefined) {
                                  return Object.entries(value).map(([spec, d], i) => renderRow(spec, d, i));
                                }
                                return renderRow(key, value, 0);
                              })}
                            </tbody>
                          </table>
                        );
                      })}
                    </>
                  )}

                  {/* ✅ BUILDING ANALYSIS WRAP */}
                  {show("buildingAnalysis") && (() => {
                    const areaSqft = getIndividualBuildingAreaSqft(Number(buildingNum)) || 1;
                    const mySelectedComponents = [
                      "leanTo", "canopy", "frameOpening", "partitionWall",
                      "cageLadder", "craneBeam", "mezzanine", "fascia",
                      "structuralLouver", "staircase",
                    ];
                    const activeComponents = mySelectedComponents.filter((compKey) => {
                      const compWeight = buildingData?.[buildingNum]?.[compKey]?.totalWeight;
                      return Number(compWeight) > 0;
                    });
                    if (activeComponents.length === 0) return null;
                    return (
                      <div className="mb-12 p-4 border rounded shadow-sm bg-white">
                        <h4 style={{ marginTop: "30px", marginBottom: "10px" }}>
                          Building {buildingNum} - Analysis
                        </h4>
                        {activeComponents.map((componentType) => {
                          const items = data?.componentBlocks?.[componentType]?.[buildingNum];
                          if (!items || items.length === 0) return null;
                          const groupSummaries = {};
                          const detailedGrouped = {};
                          items.forEach((item) => {
                            const code = item.materialCode || item.code || "";
                            let matchedGroup = "OTHERS";
                            Object.entries(MATERIAL_GROUPS).forEach(([groupKey, config]) => {
                              if (config.match(code)) matchedGroup = groupKey;
                            });
                            if (!groupSummaries[matchedGroup]) {
                              groupSummaries[matchedGroup] = {
                                label: MATERIAL_GROUPS[matchedGroup]?.label || matchedGroup,
                                totalWeight: 0,
                                totalArea: 0,
                              };
                            }
                            const weight = Number(item.totalWeight) || 0;
                            const pm = Number(item.pieceMeasure) || 0;
                            const qty = Number(item.quantity) || 0;
                            const avg = Number(item.avg) > 0 ? Number(item.avg) : 1;
                            groupSummaries[matchedGroup].totalWeight += weight;
                            groupSummaries[matchedGroup].totalArea += pm * qty * avg;
                            if (!detailedGrouped[matchedGroup]) detailedGrouped[matchedGroup] = [];
                            detailedGrouped[matchedGroup].push(item);
                          });
                          return (
                            <div key={componentType} className="mt-8 p-4 border rounded shadow-sm bg-white">
                              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                                <h4 className="font-bold text-primary text-uppercase mb-0">
                                  {componentLabels[componentType] || componentType} - Analysis
                                </h4>
                                <span className="badge badge-dark p-2">B{buildingNum}</span>
                              </div>
                              <div className="mb-6">
                                <h6 className="font-bold text-secondary mb-2">I. Material Group Summary</h6>
                                <table className="table table-sm table-bordered shadow-sm">
                                  <thead className="bg-dark text-white small text-center">
                                    <tr>
                                      <th className="text-left">Material Category</th>
                                      <th>Total Weight (Kg)</th>
                                      <th>Kg / Sqft</th>
                                    </tr>
                                  </thead>
                                  <tbody className="small">
                                    {Object.entries(groupSummaries).map(([key, g]) => (
                                      <tr key={key} className="text-center font-weight-bold">
                                        <td className="text-left bg-light">{g.label}</td>
                                        <td>{g.totalWeight.toFixed(3)}</td>
                                        <td className="text-primary">{(g.totalWeight / areaSqft).toFixed(3)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div>
                                <h6 className="font-bold text-secondary mb-2">II. Detailed Material Breakdown</h6>
                                {Object.entries(detailedGrouped).map(([groupKey, mItems]) => (
                                  <div key={groupKey} className="mb-4">
                                    <div className="bg-light p-2 border font-weight-bold small text-secondary">
                                      {MATERIAL_GROUPS[groupKey]?.label || groupKey}
                                    </div>
                                    <table className="table table-sm table-bordered bg-white mb-0">
                                      <thead className="small text-center bg-white text-muted">
                                        <tr>
                                          <th className="text-left">Description</th>
                                          <th>Code</th>
                                          <th>Weight</th>
                                          <th>Kg/Sqft</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {mItems.map((item, idx) => {
                                          const w = Number(item.totalWeight || 0);
                                          if (w <= 0 && Number(item.quantity || 0) <= 0) return null;
                                          return (
                                            <tr key={idx} className="small text-center">
                                              <td className="text-left p-2 border">{item.materialDescription || item.description}</td>
                                              <td className="p-2 border">{item.materialCode || item.code || "-"}</td>
                                              <td className="p-2 border font-weight-bold">{w.toFixed(3)}</td>
                                              <td className="p-2 border text-info">{(w / areaSqft).toFixed(3)}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* ✅ BUILDING REMARKS WRAP (inside loop) */}
                  {show("remarks") && (() => {
                    const buildingRemarks = data?.buildings?.find(
                      (b) => String(b.buildingId) === String(buildingNum)
                    );
                    if (!buildingRemarks?.remarksEstimationCreation) return null;
                    return (
                      <div style={{ border: "1px solid black", padding: "8px", marginTop: "20px" }}>
                        <strong>
                          Remarks: Building {buildingRemarks.buildingId}{" "}
                          {buildingRemarks.buildingTypes}
                        </strong>
                        <div
                          style={{ marginTop: "6px", paddingLeft: "15px" }}
                          dangerouslySetInnerHTML={{
                            __html: buildingRemarks.remarksEstimationCreation || "",
                          }}
                        />
                      </div>
                    );
                  })()}

                </div>
              );
            })}
        </div>
      </Row>

      {/* ✅ COMBINED ESTIMATION WRAP */}
      {show("combinedEstimation") && (
        <>
          <div className="compact-component-table mt-5">
            <div className="building-section">
              <h3>
                Combined Total Estimation for All Buildings ({totalBuildings}{" "}
                Buildings)
              </h3>
              <table
                border="1"
                style={{ borderCollapse: "collapse", width: "100%", marginBottom: "20px" }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <th style={{ padding: "8px", textAlign: "left" }}>SL</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>Description</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>Total Weight</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>Unit</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>Total Price</th>
                  </tr>
                </thead>
                <tbody>
                  {combinedData.map((component, index) => (
                    <tr key={index}>
                      <td style={{ padding: "8px" }}>{index + 1}</td>
                      <td style={{ padding: "8px" }}>{component.description}</td>
                      <td style={{ padding: "8px" }}>{component.totalWeight}</td>
                      <td style={{ padding: "8px" }}>KG</td>
                      <td style={{ padding: "8px" }}>{component.totalPrice}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: "#e0f7fa" }}>
                    <td colSpan="2" style={{ padding: "8px", fontWeight: "bold", textAlign: "right" }}>
                      Grand Total:
                    </td>
                    <td style={{ padding: "8px", fontWeight: "bold" }}>{combinedGrandTotalWeight}</td>
                    <td style={{ padding: "8px" }}>KG</td>
                    <td style={{ padding: "8px", fontWeight: "bold" }}>{combinedGrandTotalPrice}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <Row className="mb-3">
            <Col>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "4px", width: "50%" }}>
                      <strong>Total Buildings in Estimation:</strong>
                    </td>
                    <td className="text-right" style={{ padding: "4px", fontWeight: "bold" }}>
                      {totalBuildings}
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td style={{ padding: "4px", width: "50%" }}>
                      <strong>Total Area:</strong>
                    </td>
                    <td className="d-flex flex-column justify-content-end" style={{ padding: "4px" }}>
                      <tr className="text-right">{totalBuildingArea.toFixed(3)}</tr>
                      <tr className="text-right">{(totalBuildingArea * 10.764).toFixed(3)}</tr>
                    </td>
                    <td>
                      <tr>Sqm</tr>
                      <tr>Sqft</tr>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "4px" }}>
                      <strong>Total Weight:</strong>
                    </td>
                    <td className="d-flex flex-column justify-content-end" style={{ padding: "4px" }}>
                      <tr className="text-right">{grandTotalWeight.toFixed(3)}</tr>
                      <tr className="text-right">{(grandTotalWeight / 1000).toFixed(3)}</tr>
                    </td>
                    <td>
                      <tr>Kg</tr>
                      <tr>Tonns</tr>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "4px" }}>
                      <strong>kg per sft:</strong>
                    </td>
                    <td className="text-right" style={{ padding: "4px" }}>
                      {(grandTotalWeight / (totalBuildingArea * 10.764)).toFixed(3)}
                    </td>
                    <td>Kg/Sqft</td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        </>
      )}

      {/* ✅ COMBINED MATERIAL SUMMARY WRAP */}
      {show("combinedMaterialSummary") && (
        <div className="compact-component-table mt-5">
          <div className="building-section">
            <h3>
              Combined Material-wise Summary (All {totalBuildings} Buildings)
            </h3>
            <table
              border="1"
              style={{ borderCollapse: "collapse", width: "100%", marginBottom: "20px" }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  <th style={{ padding: "8px" }}>SL</th>
                  <th style={{ padding: "8px" }}>Material Description</th>
                  <th style={{ padding: "8px" }}>Material Code</th>
                  <th style={{ padding: "8px" }}>Total Weight (Kg)</th>
                  <th style={{ padding: "8px" }}>Kg / Sqft</th>
                </tr>
              </thead>
              <tbody>
                {groupedMaterialList.map((mat, index) => (
                  <tr key={mat.materialCode}>
                    <td style={{ padding: "8px" }}>{index + 1}</td>
                    <td style={{ padding: "8px" }}>{mat.materialCode} Summary</td>
                    <td style={{ padding: "8px" }}>{mat.materialCode}</td>
                    <td style={{ padding: "8px", fontWeight: "bold" }}>{mat.totalWeight.toFixed(3)}</td>
                    <td style={{ padding: "8px" }}>{(mat.totalWeight / totalSqftAllBuildings).toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: "#e0f7fa", fontWeight: "bold" }}>
                  <td colSpan="3" style={{ textAlign: "right", padding: "8px" }}>Grand Total</td>
                  <td style={{ padding: "8px" }}>{grandTotalWeight.toFixed(3)}</td>
                  <td style={{ padding: "8px" }}>{(grandTotalWeight / totalSqftAllBuildings).toFixed(3)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ✅ BOTTOM REMARKS WRAP (outside loop) */}
      {show("remarks") && (
        <Row>
          {data?.buildings?.map((item) => (
            <Col md={12} className="mb-2" key={item?.buildingId}>
              <div style={{ border: "1px solid black", padding: "8px", height: "100%" }}>
                <strong>
                  Remarks: Building {item?.buildingId} {item?.buildingTypes}
                </strong>
                <ol style={{ margin: "4px 0", paddingLeft: "20px" }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: item?.remarksEstimationCreation || "",
                    }}
                  ></div>
                </ol>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* Status Section - always print */}
      <Row className="mt-3">
        <Col>
          <div style={{ textAlign: "center", padding: "8px", border: "1px solid black" }}>
            <strong>Estimation Status:</strong> {data?.estimationStatus} |
            <strong> Header Status:</strong> {data?.headerStatus} |
            <strong> Version:</strong> {data?.version}
          </div>
        </Col>
      </Row>
    </div>
  );

});

export default EstimationPrint;


/* for line number 2759 if i want show instead of empty then show - and if area is there then show area with unit d.thickness ||*/